// TradeCard — one visit, on the deal page's Trades card and the Builds board's Trades view (Oct 28, 2026).
// Everything Robert needs without calling Alex: the work, the day, the footage, what was agreed, the price
// (for those who see prices), who set it up, and what needs someone. Edit, Move, Call off, Done and Cancel
// sit on the card for the people who can use them.
//
// Oct 29 (Greg): "the reps only care about the amount that we need to charge homeowner and where their
// commission starts." The price reads as the minimum to charge (the floor on the sub's price), the customer's
// price, and the dollars above the minimum (upside) or under it; never the sub's price or a margin %. Chad's
// list on the Commission board keeps the sub's price.
//
// Oct 29 (Greg): "once signed, the schedule button is available." On a signed roof job a homeowner-billed trade waits
// on the homeowner's add-on: the card says where it stands (not sent, sent, signed) with Sign, and Schedule once
// signed while the visit has no day; Move and "Give it a day" wait for the signature (the server refuses them).

import { useState } from "react";
import DealLink from "../DealLink";
import { etDate } from "../../lib/weeklySpiffs";
import {
  BILLED_TO_LABELS, MARGIN_REVIEW_LABELS, fmtMoney2, fmtTradeDay, floorOf, isAddendumOpen, isOpenTrade, orderChipPlace, repMarginReason, tradeAttention, tradeCall, upsideAbove,
  type Trade, type TradePrice,
} from "../../lib/trades";
import { TradeStatusChip, dayLine, itemsLine, workLabel } from "./tradeUi";
import type { TradeAction } from "./TradeActionModal";
import AddendumActions from "./AddendumActions";
import TradeOrderChip from "./TradeOrderChip";

interface Props {
  t: Trade;
  /** The pair partner, when there is one (its day shows on this card). */
  partner?: Trade | null;
  price: { price: TradePrice; own: boolean } | null;
  today: string;
  names: Record<string, string>;
  showJob?: boolean;
  /** Opened from a link to this trade (/deal/<id>?trade=<id>, Oct 29): ringed. */
  focused?: boolean;
  canAct: boolean;
  canDone: boolean;
  onEdit: (t: Trade) => void;
  onAction: (t: Trade, a: TradeAction) => void;
  onChanged: () => void;
}

export default function TradeCard({ t, partner, price, today, names, showJob, focused, canAct, canDone, onEdit, onAction, onChanged }: Props) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const live = isOpenTrade(t);
  const p = price?.price ?? null;
  // The job's add-on (either visit of a pair carries it): a job waiting on the homeowner gets no day yet.
  const waitsOnAddendum = isAddendumOpen(p?.addendum_status);
  const attention = tradeAttention(t, today, price?.own ? price.price : null, p);
  // The minimum to charge and the customer's price against it, at the target the trade was priced at.
  const pct = p?.margin_target_pct ?? 30;
  const floor = p ? floorOf(p.agreed_total, pct) : null;
  const up = p ? upsideAbove(p.agreed_total, p.customer_total, pct) : null;
  // A total agreed with the sub that isn't the lines' own: the minimum is on that total, said in words.
  const linesTotal = p ? Math.round(p.lines.reduce((t, l) => t + (l.amount ?? 0), 0) * 100) / 100 : null;
  const onAgreed = !!p && p.price_status === "agreed" && p.agreed_total !== null && p.agreed_total !== linesTotal;
  // The job's order in Books: with the price, the visit's own or its partner's; alone on an open visit of a JAC pair
  // whose price isn't on screen (review, Oct 29: the reset showed nothing once its done detach left the 14 days).
  const orderChipAt = orderChipPlace(t, !!p);

  async function stillGood() {
    setBusy(true);
    try {
      await tradeCall({ action: "trade_still_good", trade_id: t.id });
      onChanged();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div id={`trade-${t.id}`} className={`rounded-xl border bg-white p-3 text-sm ${t.needs_check ? "border-amber-300" : "border-slate-200"} ${live ? "" : "opacity-70"} ${focused ? "ring-2 ring-bardin-orange" : ""}`}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <TradeStatusChip t={t} />
            <span className="font-semibold text-bardin-ink">{workLabel(t)}</span>
          </div>
          {showJob && t.deal && (
            <div className="mt-0.5 text-[13px]">
              <DealLink id={t.deal_id}><span className="font-semibold text-bardin-ink">{t.deal.homeowner_name ?? "(no name)"}</span></DealLink>
              <span className="text-slate-500"> · {[t.deal.property_street, t.deal.property_city].filter(Boolean).join(", ")}</span>
            </div>
          )}
          <div className="mt-0.5 text-[12.5px] text-slate-700">
            {t.vendor && <span className="text-slate-500">{t.vendor} · </span>}
            {t.status === "complete" ? `Done ${fmtTradeDay(t.completed_date)}` : t.status === "cancelled" ? `Cancelled${t.cancel_reason ? `: ${t.cancel_reason}` : ""}` : dayLine(t)}
            {partner && (
              <span className="text-slate-500"> · {partner.trade_type === "detach" ? "detach" : partner.trade_type === "reset" ? "reset" : "install"} {partner.scheduled_date ? fmtTradeDay(partner.scheduled_date) : "has no day yet"}</span>
            )}
            {t.timing !== "none" && t.deal?.build_scheduled_date && (
              <span className="text-slate-400"> · roof {fmtTradeDay(t.deal.build_scheduled_date)}</span>
            )}
          </div>
          {itemsLine(t) && <div className="mt-0.5 text-[12px] text-slate-600">{itemsLine(t)}</div>}
        </div>
        {t.calendar_html_link && live && (
          <a href={t.calendar_html_link} target="_blank" rel="noreferrer" className="shrink-0 text-[11px] text-blue-600 hover:underline">Google ↗</a>
        )}
      </div>

      {attention && (
        <div className={`mt-2 rounded-lg px-2.5 py-1.5 text-[12px] ${t.needs_check ? "bg-amber-50 text-amber-900" : "bg-slate-50 text-slate-700"}`}>
          ⚠ {attention}
          {canAct && (
            <span className="ml-2 inline-flex gap-1.5">
              {t.needs_check && <button type="button" disabled={busy} onClick={stillGood} className="font-semibold underline">Still good</button>}
              {(t.needs_check || t.status === "pending" || (t.scheduled_date ?? "") < today) && !waitsOnAddendum && (
                <button type="button" onClick={() => onAction(t, "move")} className="font-semibold underline">{t.scheduled_date ? "Move" : "Give it a day"}</button>
              )}
            </span>
          )}
        </div>
      )}

      {t.scope_notes && (
        <div className="mt-2">
          <div className={`whitespace-pre-wrap text-[12.5px] text-slate-700 ${open ? "" : "line-clamp-3"}`}>{t.scope_notes}</div>
          {t.scope_notes.length > 160 && (
            <button type="button" onClick={() => setOpen((v) => !v)} className="text-[11px] text-slate-500 underline">{open ? "Less" : "All of what was agreed"}</button>
          )}
        </div>
      )}

      {p && (
        <div className="mt-2 text-[12px] text-slate-600">
          {price!.own
            ? <>
                {p.price_status === "quote_pending" || floor === null ? <span className="text-amber-700">Waiting on a price from the sub</span>
                  : <>Minimum <b className="text-slate-800 tabular-nums">{fmtMoney2(floor)}</b>{onAgreed && <span className="text-slate-400"> (on the total agreed with the sub)</span>}</>}
                {/* Greg, Oct 29: the price to the customer against the minimum, with Chad's answer when it was short */}
                {t.billed_to !== "bardin" && (
                  <> · {t.billed_to ? BILLED_TO_LABELS[t.billed_to].toLowerCase() : "customer"} <b className="text-slate-800 tabular-nums">{p.customer_total !== null ? fmtMoney2(p.customer_total) : "not set"}</b>
                    {up !== null && up > 0 && <span className="text-emerald-700 font-semibold"> · {fmtMoney2(up)} above the minimum</span>}
                    {up !== null && up < 0 && <span className="text-rose-700 font-semibold"> · {fmtMoney2(-up)} under the minimum</span>}
                  </>
                )}
              </>
            : <span className="text-slate-400">Priced with the {partner?.trade_type === "detach" ? "detach" : "other visit"}</span>}
          {t.billed_to && (t.billed_to === "bardin" || !price!.own) && <span className="text-slate-400"> · paid by {BILLED_TO_LABELS[t.billed_to].toLowerCase()}</span>}
          {price!.own && p.margin_review !== "none" && (
            <div className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${p.margin_review === "allowed" ? "bg-emerald-100 text-emerald-800" : p.margin_review === "deducted" ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}`}
                 title={repMarginReason(p.margin_review_reason, floor, p.customer_total, pct) ?? undefined}>
              {p.margin_review === "deducted" && p.margin_loss !== null ? `Chad sent ${fmtMoney2(p.margin_loss)} to the non-collect bucket` : MARGIN_REVIEW_LABELS[p.margin_review]}
              {p.margin_review !== "needs_review" && p.margin_review_note ? `: ${p.margin_review_note}` : ""}
            </div>
          )}
          {orderChipAt === "price" && <TradeOrderChip t={t} />}
        </div>
      )}
      {orderChipAt === "alone" && (
        <div className="mt-2 text-[12px]"><TradeOrderChip t={t} /></div>
      )}

      {p && live && p.addendum_status && p.addendum_status !== "not_needed" && (
        <div className="mt-2">
          <AddendumActions
            variant="card"
            tradeId={t.id}
            status={p.addendum_status}
            sentAt={p.addendum_sent_at ?? null}
            signedAt={p.addendum_signed_at ?? null}
            canAct={canAct}
            schedule={t.scheduled_date ? [] : [{ id: t.id, label: workLabel(t).toLowerCase() }]}
            onSchedule={() => onAction(t, "move")}
            onSent={onChanged}
          />
        </div>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-400">
        {/* The Eastern day: created_at comes back in UTC, so slicing it read tomorrow after 8 PM (review, Oct 29). */}
        <span>Set up by {t.assigned_by ? names[t.assigned_by] ?? "someone" : "hand (linked from Google)"} {fmtTradeDay(etDate(new Date(t.created_at)))}</span>
        {live && canAct && (
          <span className="ml-auto flex flex-wrap gap-x-3 text-[12px] font-semibold">
            <button type="button" onClick={() => onEdit(t)} className="text-slate-600 hover:text-slate-900">Edit</button>
            {/* a day waits for the homeowner's add-on signature (Oct 29); calling a dated visit off is fine */}
            {!waitsOnAddendum && <button type="button" onClick={() => onAction(t, "move")} className="text-blue-700 hover:text-blue-900">{t.scheduled_date ? "Move" : "Give it a day"}</button>}
            {t.scheduled_date && <button type="button" onClick={() => onAction(t, "call_off")} className="text-slate-600 hover:text-slate-900">Call off</button>}
            {canDone && <button type="button" onClick={() => onAction(t, "done")} className="text-emerald-700 hover:text-emerald-900">Done</button>}
            <button type="button" onClick={() => onAction(t, "cancel")} className="text-rose-700 hover:text-rose-900">Cancel</button>
          </span>
        )}
        {live && !canAct && canDone && (
          <button type="button" onClick={() => onAction(t, "done")} className="ml-auto text-[12px] font-semibold text-emerald-700">Done</button>
        )}
      </div>
    </div>
  );
}
